import { RJSFSchema } from "@rjsf/utils";

const coercePrefillValueForSchema = (fieldSchema: any, raw: any) => {
  const type = fieldSchema?.type;

  if (type === "string") {
    // strings are already compatible with AJV/RJSF; no coercion needed
    return raw;
  }

  // arrays (especially checkbox groups)
  if (type === "array") {
    if (raw == null) return raw;
    if (Array.isArray(raw)) return raw;
    return [raw];
  }

  // booleans
  if (type === "boolean") {
    if (typeof raw === "boolean") return raw;
    if (typeof raw === "string") {
      const s = raw.trim().toLowerCase();
      if (s === "true") return true;
      if (s === "false") return false;
    }
    return raw;
  }

  // numbers / integers
  if (type === "number" || type === "integer") {
    if (typeof raw === "number") return raw;
    if (typeof raw === "string") {
      const s = raw.trim();
      if (s === "") return undefined;
      const n = Number(s);
      return Number.isNaN(n) ? raw : n;
    }
    return raw;
  }

  return raw;
};

const extractDynamicPrefillFromMailboxPayload = (
  payload: Record<string, any>,
  schema: RJSFSchema | undefined
) => {
  if (!schema || schema.type !== "object" || !schema.properties) return {};

  const meta: Record<string, any> | null =
    payload &&
    typeof payload === "object" &&
    payload.meta &&
    typeof payload.meta === "object" &&
    !Array.isArray(payload.meta)
      ? (payload.meta as Record<string, any>)
      : null;

  // Dynamic prefill values must ONLY be read from `payload.meta`.
  // We intentionally do NOT fall back to the flat payload to avoid key collisions
  // with static mailbox fields (name/email/subject/description).
  if (!meta) return {};

  const source = meta;
  const result: Record<string, any> = {};

  for (const key of Object.keys(schema.properties)) {
    const fieldSchema: any = (schema.properties as any)[key];
    const title: string | undefined = fieldSchema?.title;

    const hasByKey = Object.prototype.hasOwnProperty.call(source, key);
    const hasByTitle =
      !!title && Object.prototype.hasOwnProperty.call(source, title);

    if (!hasByKey && !hasByTitle) continue;

    const raw = hasByKey
      ? (source as any)[key]
      : (source as any)[title as string];
    result[key] = coercePrefillValueForSchema(fieldSchema, raw);
  }

  return result;
};

const extractImmutableDynamicKeysFromMailboxPayload = (
  immutableList: unknown,
  schema: RJSFSchema | undefined
) => {
  if (!schema || schema.type !== "object" || !schema.properties)
    return new Set<string>();

  const wantedRaw = Array.isArray(immutableList)
    ? (immutableList.filter((x) => typeof x === "string") as string[])
    : [];

  if (wantedRaw.length === 0) return new Set<string>();

  // Map titles to schema keys
  const byTitle = new Map<string, string>();
  for (const key of Object.keys(schema.properties)) {
    const fieldSchema: any = (schema.properties as any)[key];
    const title: string | undefined = fieldSchema?.title;
    if (title) byTitle.set(title, key);
  }

  const wanted = wantedRaw
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (s.startsWith("meta.") ? s.slice("meta.".length) : s));

  const out = new Set<string>();

  for (const item of wanted) {
    // allow either the schema property key or the title
    if ((schema.properties as any)[item]) {
      out.add(item);
      continue;
    }
    const mapped = byTitle.get(item);
    if (mapped) out.add(mapped);
  }

  return out;
};

export {
  coercePrefillValueForSchema,
  extractDynamicPrefillFromMailboxPayload,
  extractImmutableDynamicKeysFromMailboxPayload,
};
